import React from 'react';
import { Phone, MessageCircle, Star } from 'lucide-react';
import { businessInfo } from '../mockData';
import { Button } from './ui/button';

export const HeroSection = () => {
  const scrollToForm = () => {
    const element = document.getElementById('contact-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${businessInfo.whatsappNumber}`, '_blank');
  };

  return (
    <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Insurance Bonus Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-3 rounded-xl shadow-lg">
            <span className="text-sm sm:text-base font-bold">
              🎁 Kostenlose KFZ-Versicherungsberatung inkl. bis zu 20% Neukundenrabatt
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* NEW Badge */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full shadow-lg mb-4 animate-pulse">
              <span className="text-lg font-bold">⚡ NEU</span>
              <span className="text-sm font-semibold">Schnellzulassung in 20 Minuten!</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="text-green-600">Schnellzulassung</span> in nur 20 Minuten{' '}
              <span className="text-blue-600">direkt vor Ort!</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
              Fahren Sie Ihr Fahrzeug innerhalb von 20 Minuten direkt von uns zugelassen. 
              Keine Wartezeiten, kein Behördengang – alles aus einer Hand!
            </p>

            {/* Features List */}
            <div className="bg-white rounded-xl p-6 shadow-lg mb-8 border-2 border-green-100">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xl">⚡</span>
                  </div>
                  <span className="font-semibold text-gray-700">20 Min. fertig</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-xl">✓</span>
                  </div>
                  <span className="font-semibold text-gray-700">Alles inklusive</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xl">€</span>
                  </div>
                  <span className="font-semibold text-gray-700">Nur 149,90€</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-xl">📍</span>
                  </div>
                  <span className="font-semibold text-gray-700">Direkt vor Ort</span>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-blue-100 mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-700">
                {businessInfo.rating} Bewertung bei Google
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button 
                onClick={scrollToForm}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Jetzt Schnellzulassung buchen
              </Button>
              <Button 
                onClick={openWhatsApp}
                size="lg"
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Direkt per WhatsApp
              </Button>
            </div>

            {/* Phone Number */}
            <div className="flex items-center space-x-3 text-gray-700">
              <div className="bg-blue-100 p-3 rounded-full">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rufen Sie uns an:</p>
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
                >
                  {businessInfo.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1727893512947-8bdc773ceb02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxjYXIlMjBkZWFsZXJzaGlwJTIwcHJvZmVzc2lvbmFsJTIwaGFuZHNoYWtlJTIwa2V5c3xlbnwwfHx8fDE3NzkxOTk4NTd8MA&ixlib=rb-4.1.0&q=85"
                alt="KFZ Schnellzulassung - Schlüsselübergabe Service"
                className="w-full h-[400px] sm:h-[500px] lg:h-[600px] object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 to-transparent"></div>
            </div>

            {/* Floating Info Card */}
            <div className="absolute bottom-6 left-6 right-6 bg-white rounded-xl shadow-xl p-4 sm:p-6 backdrop-blur-sm bg-white/95">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">20</p>
                  <p className="text-xs sm:text-sm text-gray-600">Minuten</p>
                </div>
                <div className="border-l border-r border-gray-200">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">150+</p>
                  <p className="text-xs sm:text-sm text-gray-600">Zufriedene Kunden</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">149,90€</p>
                  <p className="text-xs sm:text-sm text-gray-600">Komplett-Preis</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
